import { useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useToast } from '@/components/common/Toast';

const UNDO_DELAY_MS = 5000;

interface PendingDelete {
  timerId: ReturnType<typeof setTimeout>;
  slotId: string;
}

/**
 * Hook for undoable slot deletion.
 *
 * On delete: optimistically removes the slot from React Query cache,
 * shows an undo toast for 5 seconds. If user clicks Undo, the timer
 * is cancelled and the cache is restored via refetch. If the timer
 * expires, the actual API delete fires.
 */
export function useUndoableDelete() {
  const queryClient = useQueryClient();
  const { withAction, error: showError } = useToast();
  const pendingDeletes = useRef<Map<string, PendingDelete>>(new Map());

  const deleteSlotWithUndo = useCallback(
    (slotId: string, label?: string) => {
      // Cancel any existing pending delete for this slot
      const existing = pendingDeletes.current.get(slotId);
      if (existing) {
        clearTimeout(existing.timerId);
        pendingDeletes.current.delete(slotId);
      }

      // Snapshot current timeslots cache for rollback
      const queryCache = queryClient.getQueriesData<any[]>({ queryKey: ['timeslots'] });

      // Optimistically remove the slot from all matching timeslots queries
      queryClient.setQueriesData<any[]>({ queryKey: ['timeslots'] }, (old) => {
        if (!old) return old;
        // If the data is an array of slots directly
        if (Array.isArray(old)) {
          return old.filter((s: any) => s.id !== slotId);
        }
        return old;
      });

      // Also invalidate availability so remaining windows recalculate
      queryClient.invalidateQueries({ queryKey: ['availability'] });

      // Set up the delayed actual delete
      const timerId = setTimeout(async () => {
        pendingDeletes.current.delete(slotId);
        try {
          await api.delete(`/timeslots/${slotId}`);
          queryClient.invalidateQueries({ queryKey: ['timeslots'] });
          queryClient.invalidateQueries({ queryKey: ['availability'] });
        } catch (err: any) {
          showError(err.message || 'Failed to delete slot');
          // Restore cache on failure
          for (const [key, data] of queryCache) {
            queryClient.setQueryData(key, data);
          }
          queryClient.invalidateQueries({ queryKey: ['timeslots'] });
          queryClient.invalidateQueries({ queryKey: ['availability'] });
        }
      }, UNDO_DELAY_MS);

      pendingDeletes.current.set(slotId, { timerId, slotId });

      // Show undo toast
      withAction(
        label || 'Slot removed',
        {
          label: 'Undo',
          onClick: () => {
            const pending = pendingDeletes.current.get(slotId);
            if (pending) {
              clearTimeout(pending.timerId);
              pendingDeletes.current.delete(slotId);
            }
            // Restore the old cache data
            for (const [key, data] of queryCache) {
              queryClient.setQueryData(key, data);
            }
            queryClient.invalidateQueries({ queryKey: ['timeslots'] });
            queryClient.invalidateQueries({ queryKey: ['availability'] });
          },
        },
        { type: 'warning', duration: UNDO_DELAY_MS }
      );
    },
    [queryClient, withAction, showError]
  );

  return { deleteSlotWithUndo };
}
